/**
 * <EditProfileTab />
 *
 * Lets the authenticated user edit their display name, email, and
 * password.  Username is immutable (stable forum mentions + storefront
 * URLs); role is managed via subscription / admin.
 */
import * as React from "react";
import { CheckCircle2, KeyRound, UserRoundCog } from "lucide-react";
import { api, extractApiError } from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "../../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../../components/ui/Card";
import { Input } from "../../../components/ui/Input";

export function EditProfileTab() {
  const { user, refresh } = useAuth();
  const [form, setForm] = React.useState({
    full_name: user?.full_name ?? "",
    email: user?.email ?? "",
    bio: user?.bio ?? "",
  });
  const [profileBusy, setProfileBusy] = React.useState(false);
  const [profileMsg, setProfileMsg] = React.useState<string | null>(null);
  const [profileErr, setProfileErr] = React.useState<string | null>(null);

  const [pwd, setPwd] = React.useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [pwdBusy, setPwdBusy] = React.useState(false);
  const [pwdMsg, setPwdMsg] = React.useState<string | null>(null);
  const [pwdErr, setPwdErr] = React.useState<string | null>(null);

  const setField = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileBusy(true);
    setProfileMsg(null);
    setProfileErr(null);
    try {
      const body: Record<string, string> = {};
      // Only send fields that actually changed so the server doesn't
      // bump updated_at for no reason.
      if (form.full_name !== (user?.full_name ?? "")) body.full_name = form.full_name;
      if (form.email.toLowerCase() !== (user?.email ?? "").toLowerCase())
        body.email = form.email.toLowerCase();
      if (form.bio !== (user?.bio ?? "")) body.bio = form.bio;
      if (Object.keys(body).length === 0) {
        setProfileMsg("Nothing to update.");
        setProfileBusy(false);
        return;
      }
      await api.patch("/users/me", body);
      await refresh();
      setProfileMsg("Profile saved.");
    } catch (err) {
      setProfileErr(extractApiError(err));
    } finally {
      setProfileBusy(false);
    }
  };

  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwdBusy(true);
    setPwdMsg(null);
    setPwdErr(null);
    if (pwd.new_password.length < 8 || !/[a-zA-Z]/.test(pwd.new_password) || !/[0-9]/.test(pwd.new_password)) {
      setPwdErr("New password must be at least 8 characters, with a letter and a number.");
      setPwdBusy(false);
      return;
    }
    if (pwd.new_password !== pwd.confirm) {
      setPwdErr("Passwords do not match.");
      setPwdBusy(false);
      return;
    }
    try {
      await api.post("/users/me/password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      setPwd({ current_password: "", new_password: "", confirm: "" });
      setPwdMsg("Password updated.");
    } catch (err) {
      setPwdErr(extractApiError(err));
    } finally {
      setPwdBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="edit-profile-tab">
      {/* -------------------------------------------------- Profile --- */}
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <UserRoundCog className="h-5 w-5" aria-hidden /> Account details
            </h2>
            <p className="text-ink-muted text-sm mt-1">
              Username <span className="font-mono font-semibold text-ink">@{user?.username}</span>{" "}
              is permanent. Everything else here is yours to change.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={saveProfile} className="flex flex-col gap-4" data-testid="edit-profile-form">
            <Input
              label="Full name"
              value={form.full_name}
              onChange={setField("full_name")}
              placeholder="Your display name"
              data-testid="edit-profile-fullname"
            />
            <Input
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={setField("email")}
              data-testid="edit-profile-email"
            />
            <div>
              <label
                htmlFor="edit-profile-bio"
                className="mb-1.5 block font-semibold text-ink text-sm"
              >
                Bio (optional)
              </label>
              <textarea
                id="edit-profile-bio"
                maxLength={500}
                rows={3}
                value={form.bio}
                onChange={setField("bio")}
                placeholder="A sentence or two about what you work on."
                data-testid="edit-profile-bio"
                className="w-full rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base text-ink placeholder:text-ink-subtle outline-none transition-colors focus:border-ink"
              />
            </div>

            {profileErr && (
              <p
                className="text-sm font-semibold text-brand"
                data-testid="edit-profile-error"
              >
                {profileErr}
              </p>
            )}
            {profileMsg && (
              <p
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"
                data-testid="edit-profile-saved"
              >
                <CheckCircle2 className="h-4 w-4 text-brand" /> {profileMsg}
              </p>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={profileBusy}
                data-testid="edit-profile-save"
              >
                Save changes
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      {/* -------------------------------------------------- Password --- */}
      <Card elevated>
        <CardHeader>
          <div>
            <h2 className="text-xl font-bold inline-flex items-center gap-2">
              <KeyRound className="h-5 w-5" aria-hidden /> Change password
            </h2>
            <p className="text-ink-muted text-sm mt-1">
              You'll stay signed in on this device after the update.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={savePassword} className="flex flex-col gap-4" data-testid="edit-profile-password-form">
            <Input
              label="Current password"
              type="password"
              required
              value={pwd.current_password}
              onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })}
              data-testid="edit-profile-current-pwd"
            />
            <Input
              label="New password"
              type="password"
              required
              minLength={8}
              value={pwd.new_password}
              onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })}
              hint="At least 8 characters, including a letter and a number."
              data-testid="edit-profile-new-pwd"
            />
            <Input
              label="Confirm new password"
              type="password"
              required
              minLength={8}
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              data-testid="edit-profile-confirm-pwd"
            />

            {pwdErr && (
              <p
                className="text-sm font-semibold text-brand"
                data-testid="edit-profile-password-error"
              >
                {pwdErr}
              </p>
            )}
            {pwdMsg && (
              <p
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink"
                data-testid="edit-profile-password-saved"
              >
                <CheckCircle2 className="h-4 w-4 text-brand" /> {pwdMsg}
              </p>
            )}

            <div className="flex justify-end">
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={pwdBusy}
                data-testid="edit-profile-password-save"
              >
                Update password
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
